import axios from 'axios';
import { apiBase } from '../utils/config';
import CryptoJS from "crypto-js";
import stringify from "fast-json-stable-stringify";

const SECRET_KEY =
  process.env.REACT_APP_SIGNATURE_SECRET || "mySuperSecretKey_1236243";

const instance = axios.create({
  baseURL: apiBase,
  responseType: "json",
  withCredentials: true,
});

const generateSignatureHeaders = (method, url, params, data) => {
  const timestamp = Date.now().toString();

  let cleanUrl = url || "/";

  // ✅ Remove base URL if full URL is passed
  if (cleanUrl.startsWith("http") || cleanUrl.startsWith("https")) {
    const parsed = new URL(cleanUrl);
    cleanUrl = parsed.pathname;
  }

  if (!cleanUrl.startsWith("/")) {
    cleanUrl = "/" + cleanUrl;
  }

  cleanUrl = decodeURIComponent(cleanUrl);

  // ✅ Normalize query params
  const normalizedParams = {};

  Object.keys(params || {}).forEach((key) => {
    let val = params[key];

    if (typeof val === "string") {
      val = decodeURIComponent(val);
    }

    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      normalizedParams[key] = val;
    } else if (Array.isArray(val)) {
      normalizedParams[key] = val.map((v) =>
        typeof v === "string" ? decodeURIComponent(v) : String(v),
      );
    } else if (val !== undefined && val !== null) {
      normalizedParams[key] = String(val);
    }
  });

  const queryStr = stringify(normalizedParams);

  // ✅ Normalize body
  let bodyStr = "{}";

  if (data && !(data instanceof FormData)) {
    let normalizedData = data;

    // axios may already stringify body
    if (typeof normalizedData === "string") {
      try {
        normalizedData = JSON.parse(normalizedData);
      } catch (e) { }
    }

    bodyStr = stringify(normalizedData);
  }

  const payload = `${method.toUpperCase()}:${cleanUrl}:${queryStr}:${bodyStr}:${timestamp}`;

  const signature = CryptoJS.HmacSHA256(payload, SECRET_KEY).toString(
    CryptoJS.enc.Hex
  );

  return {
    "x-signature": signature,
    "x-timestamp": timestamp,
  };
};

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const method = config.method || "GET";
  const url = config.url || "";
  const params = config.params || {};
  const data = config.data || {};

  const signatureHeaders = generateSignatureHeaders(
    method,
    url,
    params,
    data
  );

  config.headers["x-signature"] = signatureHeaders["x-signature"];
  config.headers["x-timestamp"] = signatureHeaders["x-timestamp"];

  return config;
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  refreshQueue = [];
};

const forceLogout = () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refreshValue');
    localStorage.removeItem('ERPLoginData');
    localStorage.removeItem('loggedIn');
  } catch (e) { }
  if (window?.location && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    if ((originalRequest.url || '').includes('/authentication/refreshToken')) {
      forceLogout();
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(() => instance(originalRequest));
    }

    isRefreshing = true;
    try {
      const res = await instance.post('/authentication/refreshToken', { RefreshToken: refreshToken });
      const newAccess = res?.data?.data?.Token;
      const newRefresh = res?.data?.data?.RefreshToken;
      if (!newAccess) throw new Error('No token in refresh response');

      localStorage.setItem('access_token', newAccess);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      try {
        const stored = JSON.parse(localStorage.getItem('ERPLoginData') || '{}');
        stored.Token = newAccess;
        if (newRefresh) stored.RefreshToken = newRefresh;
        localStorage.setItem('ERPLoginData', JSON.stringify(stored));
      } catch (e) { }

      processQueue(null, newAccess);
      return instance(originalRequest);
    } catch (err) {
      processQueue(err, null);
      forceLogout();
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

const request = (method, url, data) => {
  return new Promise((resolve, reject) => {
    (() => {
      if (method === 'get') {
        return instance.request({
          url, method, params: data
        });
      } else {
        return instance.request({
          url, method, data
        });
      }
    })()
      .then((res) => {
        resolve({
          ...res.data,
          statusCode: res.status
        });
      })
      .catch((err) => {
        const body = (err?.response?.data && typeof err.response.data === 'object')
          ? err.response.data
          : {};
        reject({
          ...body,
          success: false,
          message: err?.response?.data?.message || "Something went wrong",
          statusCode: err?.response?.status || 500
        });
      });
  });
};

export default {
  get: (endpoint, data) => {
    return request('get', endpoint, data);
  },
  post: (endpoint, data) => {
    return request('post', endpoint, data);
  },
  put: (endpoint, data) => {
    return request('put', endpoint, data);
  },
  del: (endpoint, data) => {
    return request('delete', endpoint, data);
  },
};
